import { preloadQuery } from "@relay";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { type PreloadedQuery, usePreloadedQuery } from "react-relay";
import { fetchQuery, graphql } from "relay-runtime";
import { AnonymousLockAccess, AnonymousLockAccessError } from "~/components/lock/AnonymousLockAccess";
import { appendSearch, getLockServicePath } from "~/components/lock/lockServiceRoutes";
import type { legacyLockMyGrantSubtypeQuery } from "./__generated__/legacyLockMyGrantSubtypeQuery.graphql";
import type { lockAnonymousMyLockQuery } from "./__generated__/lockAnonymousMyLockQuery.graphql";

const subtypeQuery = graphql`
	query legacyLockMyGrantSubtypeQuery($accessToken: String!) {
		anonymousUtilizationGrant(accessToken: $accessToken) {
			reservation {
				subtype {
					__typename
				}
			}
		}
	}
`;

const anonymousQuery = graphql`
	query lockAnonymousMyLockQuery($accessToken: String!) @relay_test_operation {
		anonymousUtilizationGrant(accessToken: $accessToken) @catch(to: NULL) {
			...AnonymousLockAccessFragment
		}
	}
`;

type LoaderData = {
	token: string;
	queryRef: PreloadedQuery<lockAnonymousMyLockQuery>;
};

export const Route = createFileRoute("/$hubMetaName/lock/my")({
	component: RouteComponent,
	errorComponent: ({ error }) => <AnonymousLockAccessError error={error} service="LOCK_B2C" />,
	loader: async (ctx): Promise<LoaderData> => {
		const token = new URLSearchParams(ctx.location.searchStr).get("token");
		if (!token) {
			throw redirect({
				href: appendSearch(`/${ctx.params.hubMetaName}/swap/my`, ctx.location.searchStr),
				replace: true,
			});
		}

		const data = await fetchQuery<legacyLockMyGrantSubtypeQuery>(ctx.context.relayEnvironment, subtypeQuery, {
			accessToken: token,
		}).toPromise();
		const subtype = data?.anonymousUtilizationGrant?.reservation.subtype.__typename;
		if (subtype === "B2BlockReservationSubtype") {
			throw redirect({
				href: appendSearch(`${getLockServicePath(ctx.params.hubMetaName, subtype)}/my`, ctx.location.searchStr),
				replace: true,
			});
		}

		return {
			token,
			queryRef: await preloadQuery<lockAnonymousMyLockQuery>(ctx.context.relayEnvironment, anonymousQuery, { accessToken: token }),
		};
	},
});

function RouteComponent() {
	const loaderData = Route.useLoaderData();
	const { anonymousUtilizationGrant } = usePreloadedQuery<lockAnonymousMyLockQuery>(anonymousQuery, loaderData.queryRef);
	return <AnonymousLockAccess token={loaderData.token} grant={anonymousUtilizationGrant ?? undefined} service="LOCK_B2C" />;
}
